import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@hqo/ui-atoms';

import { cn } from '@hqo/react-hooks';
import { createContext, useCallback, useContext } from 'react';
import { useMobileScreen } from 'app/shared/hooks/use-mobile-screen/use-mobile-screen.hook';

interface ModalProps {
  isOpen: boolean;
  /** sr-only a11y label. Visible heading goes in `<Modal.HeaderTitle>`. */
  title: string;
  /** Optional sr-only a11y description. */
  description?: string | React.ReactNode;
  contentClassName?: string;
  children: React.ReactNode;
  onClose: () => void;
}

// Lets the slots know which root they render in: the sticky Footer only makes
// sense inside the drawer's scroll column (see ModalRoot).
const ModalDrawerContext = createContext(false);

/**
 * Compound-component wrapper around `@hqo/ui-atoms` Dialog (desktop)
 * and Drawer (mobile, max-width 768px). The picker swaps the root
 * primitive but the slot API (`Modal.Header` / `Modal.HeaderTitle` /
 * `Modal.HeaderDescription` / `Modal.Body` / `Modal.Footer`) stays
 * identical so consumers don't branch.
 *
 *   <Modal isOpen={...} title="..." onClose={...}>
 *     <Modal.Header>
 *       <Modal.HeaderTitle>...</Modal.HeaderTitle>
 *       <Modal.HeaderDescription>...</Modal.HeaderDescription>
 *     </Modal.Header>
 *     <Modal.Body>...content...</Modal.Body>
 *     <Modal.Footer>...buttons...</Modal.Footer>
 *   </Modal>
 *
 * `DialogContent` ships its own X close button; the Drawer does not,
 * so we add a `DrawerClose` with a top-right X for parity with the
 * desktop UX (swipe-to-dismiss still works as the native gesture).
 */
const ModalRoot = ({
  isOpen,
  title,
  description,
  contentClassName,
  children,
  onClose,
}: ModalProps): JSX.Element => {
  const { isMobileScreen } = useMobileScreen();
  // Radix + Vaul both fire `onOpenChange(false)` on close gestures —
  // we never use a Trigger so the `true` branch is unreachable.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose],
  );

  if (isMobileScreen) {
    return (
      <Drawer fixed repositionInputs open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerContent
          className={cn(
            'max-h-[90vh] overflow-hidden pb-2 [&_.bg-muted]:bg-system-grey-light',
            contentClassName,
          )}
          data-testid="modal-root-drawer"
        >
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          {description !== undefined && (
            <DrawerDescription className="sr-only">{description}</DrawerDescription>
          )}
          {/*
            Scroll fallback. In normal use the Body owns the scroll and Header /
            Footer stay put, exactly as before. At a large system font size on a
            short screen the Header + Footer alone can outgrow the drawer's max
            height; the drawer root clips (`overflow-hidden`), so the Footer —
            the actions — used to vanish. This column scrolls instead and the
            Footer is sticky, so the actions stay reachable. It lives on an inner
            element because vaul sets `touch-action: none` on the drawer root,
            which would block touch scrolling there.
          */}
          <div
            className="flex min-h-0 flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
            data-testid="modal-drawer-scroll"
          >
            <ModalDrawerContext.Provider value>{children}</ModalDrawerContext.Provider>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn('p-0 sm:max-w-md', contentClassName)}
        data-testid="modal-root-dialog"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {description !== undefined && (
          <DialogDescription className="sr-only">{description}</DialogDescription>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
};

interface SlotProps {
  children: React.ReactNode;
  className?: string;
}

// Plain divs (not DialogHeader/DialogFooter) so the slots work inside
// both the Dialog and the Drawer root.
const ModalHeader = ({ children, className }: SlotProps): JSX.Element => (
  <div className={cn('flex flex-col gap-1 px-6 pt-6 pb-2', className)}>{children}</div>
);

const ModalHeaderTitle = ({ children, className }: SlotProps): JSX.Element => (
  <p
    className={cn('text-lg font-semibold leading-tight text-foreground', className)}
    data-testid="modal-header-title"
  >
    {children}
  </p>
);

const ModalHeaderDescription = ({ children, className }: SlotProps): JSX.Element => (
  <p
    className={cn('text-sm text-system-grey-dark', className)}
    data-testid="modal-header-description"
  >
    {children}
  </p>
);

const ModalBody = ({ children, className }: SlotProps): JSX.Element => (
  <div
    className={cn(
      // Body owns the scroll so the header / footer stay pinned and the
      // drawer root can keep vaul's drag gestures unmolested.
      'overflow-y-auto px-6 pb-2 text-sm text-system-grey-dark',
      className,
    )}
  >
    {children}
  </div>
);

const ModalFooter = ({ children, className }: SlotProps): JSX.Element => {
  const isDrawer = useContext(ModalDrawerContext);
  return (
    <div
      className={cn(
        'flex flex-col gap-2 px-6 pb-6 pt-4 sm:flex-row sm:justify-end',
        // Sticky + opaque, drawer only: kicks in when the drawer's scroll
        // fallback is scrolling (see ModalRoot) — otherwise a no-op. Never on
        // the Dialog: it has no scroll container, so sticky resolves against
        // the viewport before the Dialog's translate-y(-50%) centring, and on
        // a tall dialog the Footer lands mid-content over the Body.
        isDrawer && 'sticky bottom-0 bg-background',
        '[&_button]:min-h-[45px] md:[&_button]:min-h-[38px]',
        className,
      )}
      data-testid="modal-footer"
    >
      {children}
    </div>
  );
};

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  HeaderTitle: ModalHeaderTitle,
  HeaderDescription: ModalHeaderDescription,
  Body: ModalBody,
  Footer: ModalFooter,
});
